import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getGroupById, getActiveMembers } from '../services/groupService';
import { getMatchById, approveMatch as approveMatchService } from '../services/matchService';
import { getUsersByIds } from '../services/userService';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { matchTypeBadgeClass, MATCH_TYPE_LABELS } from '../utils/matchDisplay';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  MATCHES_FIELDS,
  MATCH_STATUS,
  MATCH_TYPES,
  SET_FIELDS,
  SET_FIELD_VALUES,
  MATCH_TEAM_FIELDS,
  USERS_FIELDS,
} from '../domain/dataModel';

const MATCH_STATUS_LABELS = { pending_approval: 'In approvazione', approved: 'Approvata', rejected: 'Rifiutata' };

function IconArrowLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function wasUserOnCourt(match, userId) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    if ([t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].includes(userId)) return true;
  }
  return false;
}

function getRequiredApprovals(match, members) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  const playerIds = new Set();
  sets.forEach((s) => {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].forEach((id) => id && playerIds.add(id));
  });
  const guestMemberIds = new Set(members.filter((m) => m[GROUP_MEMBERS_FIELDS.IS_GUEST]).map((m) => m.id));
  let realPlayers = 0;
  playerIds.forEach((id) => { if (!guestMemberIds.has(id)) realPlayers++; });
  return Math.min(3, realPlayers);
}

export default function MatchDetail() {
  const { groupId, matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const backUrl = `/groups/${groupId}${location.state?.fromSearch || ''}`;

  const [match, setMatch] = useState(null);
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);

  const isOwner = group && user?.uid && group[GROUPS_FIELDS.OWNER_ID] === user.uid;

  useEffect(() => {
    if (!groupId || !matchId) {
      setError('Parametri mancanti');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [groupData, matchData, membersList] = await Promise.all([
          getGroupById(groupId),
          getMatchById(matchId),
          getActiveMembers(groupId),
        ]);
        if (cancelled) return;
        if (!groupData) {
          setError('Gruppo non trovato');
          setLoading(false);
          return;
        }
        if (!matchData) {
          setError('Partita non trovata');
          setLoading(false);
          return;
        }
        setGroup(groupData);
        setMatch(matchData);
        setMembers(membersList);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Errore nel caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, matchId]);

  useEffect(() => {
    if (!matchId) return;
    const unsub = onSnapshot(doc(db, COLLECTIONS.MATCHES, matchId), (snap) => {
      if (snap.exists()) setMatch({ id: snap.id, ...snap.data() });
    });
    return () => unsub();
  }, [matchId]);

  const playerIds = useMemo(() => {
    if (!match) return [];
    const ids = new Set();
    const sets = match[MATCHES_FIELDS.SETS] || [];
    sets.forEach((s) => {
      const t1 = s[SET_FIELDS.TEAM1] || {};
      const t2 = s[SET_FIELDS.TEAM2] || {};
      [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].forEach((id) => id && ids.add(id));
    });
    const approvals = match[MATCHES_FIELDS.APPROVALS] || [];
    approvals.forEach((a) => a.userId && ids.add(a.userId));
    return [...ids].filter(Boolean);
  }, [match]);
  const playerIdsKey = useMemo(() => playerIds.join(','), [playerIds]);

  useEffect(() => {
    if (playerIds.length === 0 || members.length === 0) {
      setUsersMap({});
      return;
    }
    const guestIds = new Set(members.filter((m) => m[GROUP_MEMBERS_FIELDS.IS_GUEST]).map((m) => m.id));
    const userIds = playerIds.filter((pid) => !guestIds.has(pid));
    const map = {};
    members.filter((m) => m[GROUP_MEMBERS_FIELDS.IS_GUEST]).forEach((m) => {
      map[m.id] = { displayName: m[GROUP_MEMBERS_FIELDS.GUEST_NICKNAME] || 'Ospite', email: null, isGuest: true };
    });
    if (userIds.length === 0) {
      setUsersMap(map);
      return;
    }
    getUsersByIds(userIds).then((usersData) => {
      userIds.forEach((uid) => {
        const u = usersData[uid];
        map[uid] = u
          ? { displayName: u[USERS_FIELDS.DISPLAY_NAME] || u[USERS_FIELDS.EMAIL] || uid, email: u[USERS_FIELDS.EMAIL], isGuest: false }
          : { displayName: uid, email: null, isGuest: false };
      });
      setUsersMap(map);
    });
  }, [playerIdsKey, playerIds, members]);

  const displayName = (uid) => usersMap[uid]?.displayName || uid;
  const isGuest = (uid) => usersMap[uid]?.isGuest || members.some((m) => m.id === uid && m[GROUP_MEMBERS_FIELDS.IS_GUEST]);

  const avatarColorFromUserId = (userId) => {
    if (!userId) return undefined;
    let h = 0;
    for (let i = 0; i < userId.length; i++) h = (h << 5) - h + userId.charCodeAt(i);
    const hue = Math.abs(h % 360);
    return `hsl(${hue}, 55%, 45%)`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    return name.slice(0, 2).toUpperCase();
  };

  const handleApprove = async () => {
    if (!match || !user?.uid) return;
    setApproveLoading(true);
    setError(null);
    try {
      await approveMatchService(match.id, user.uid, members);
    } catch (err) {
      setError(err.message || 'Errore durante l\'approvazione');
    } finally {
      setApproveLoading(false);
    }
  };

  const playerStats = useMemo(() => {
    if (!match) return [];
    const sets = match[MATCHES_FIELDS.SETS] || [];
    const stats = {};
    sets.forEach((s) => {
      const t1 = s[SET_FIELDS.TEAM1] || {};
      const t2 = s[SET_FIELDS.TEAM2] || {};
      const score1 = Number(s[SET_FIELDS.SCORE1]) || 0;
      const score2 = Number(s[SET_FIELDS.SCORE2]) || 0;
      const team1Won = score1 > score2;
      [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean).forEach((pid) => {
        if (!stats[pid]) stats[pid] = { setsPlayed: 0, setsWon: 0, gamesWon: 0 };
        stats[pid].setsPlayed++;
        if (team1Won) stats[pid].setsWon++;
        stats[pid].gamesWon += score1;
      });
      [t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean).forEach((pid) => {
        if (!stats[pid]) stats[pid] = { setsPlayed: 0, setsWon: 0, gamesWon: 0 };
        stats[pid].setsPlayed++;
        if (!team1Won) stats[pid].setsWon++;
        stats[pid].gamesWon += score2;
      });
    });
    return Object.entries(stats)
      .map(([playerId, s]) => ({ playerId, ...s }))
      .sort((a, b) => b.setsWon - a.setsWon || b.gamesWon - a.gamesWon);
  }, [match]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  if (error || !match || !group) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="outline" onClick={() => navigate(backUrl)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Torna al gruppo
        </Button>
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error || 'Partita non trovata'}
        </div>
      </div>
    );
  }

  const matchType = match[MATCHES_FIELDS.TYPE] || MATCH_TYPES.CLASSIC;
  const matchStatus = match[MATCHES_FIELDS.STATUS] || MATCH_STATUS.PENDING_APPROVAL;
  const playedAt = match[MATCHES_FIELDS.PLAYED_AT];
  const approvals = match[MATCHES_FIELDS.APPROVALS] || [];
  const requiredApprovals = getRequiredApprovals(match, members);
  const hasAlreadyApproved = user?.uid && approvals.some((a) => a.userId === user.uid);
  const wasInMatch = user?.uid ? wasUserOnCourt(match, user.uid) : false;
  const canApprove = user?.uid && (wasInMatch || isOwner) && !hasAlreadyApproved && matchStatus === MATCH_STATUS.PENDING_APPROVAL;
  const sets = match[MATCHES_FIELDS.SETS] || [];

  const renderSetCard = (s, idx, label) => {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    const score1 = s[SET_FIELDS.SCORE1] ?? 0;
    const score2 = s[SET_FIELDS.SCORE2] ?? 0;
    const team1Won = score1 > score2;
    const team1Names = [displayName(t1[MATCH_TEAM_FIELDS.PLAYER1]), displayName(t1[MATCH_TEAM_FIELDS.PLAYER2])].filter(Boolean).join(' + ');
    const team2Names = [displayName(t2[MATCH_TEAM_FIELDS.PLAYER1]), displayName(t2[MATCH_TEAM_FIELDS.PLAYER2])].filter(Boolean).join(' + ');
    return (
      <div key={idx} className="mb-3 rounded-card border border-surface-muted bg-surface-card p-4">
        <p className="mb-3 text-xs text-brand-ice/50">{label || `Set ${idx + 1}`}</p>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="text-center">
            <p className={`font-medium ${team1Won ? 'text-brand-ice font-bold' : 'text-brand-ice/60'}`}>{team1Names}</p>
            {team1Won && <span className="mt-1 block text-xs text-win">🏆 Vincitori</span>}
          </div>
          <p className="font-display text-3xl font-bold text-brand-ice tabular-nums">{score1} - {score2}</p>
          <div className="text-center">
            <p className={`font-medium ${!team1Won && score2 > score1 ? 'text-brand-ice font-bold' : 'text-brand-ice/60'}`}>{team2Names}</p>
            {!team1Won && score2 > score1 && <span className="mt-1 block text-xs text-win">🏆 Vincitori</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderKingSummary = (setsToUse, title) => {
    const stats = {};
    setsToUse.forEach((s) => {
      const t1 = s[SET_FIELDS.TEAM1] || {};
      const t2 = s[SET_FIELDS.TEAM2] || {};
      const score1 = Number(s[SET_FIELDS.SCORE1]) || 0;
      const score2 = Number(s[SET_FIELDS.SCORE2]) || 0;
      const team1Won = score1 > score2;
      [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean).forEach((pid) => {
        if (!stats[pid]) stats[pid] = { setsWon: 0, gamesWon: 0 };
        if (team1Won) stats[pid].setsWon++;
        stats[pid].gamesWon += score1;
      });
      [t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].filter(Boolean).forEach((pid) => {
        if (!stats[pid]) stats[pid] = { setsWon: 0, gamesWon: 0 };
        if (!team1Won) stats[pid].setsWon++;
        stats[pid].gamesWon += score2;
      });
    });
    const sorted = Object.entries(stats)
      .map(([playerId, s]) => ({ playerId, ...s }))
      .sort((a, b) => b.setsWon - a.setsWon || b.gamesWon - a.gamesWon);
    return (
      <div className="mt-3 space-y-2">
        <h3 className="text-sm font-semibold text-brand-ice/70">{title}</h3>
        {sorted.map((row, i) => (
          <div key={row.playerId} className={`flex items-center justify-between rounded-card px-3 py-2 ${i === 0 ? 'border border-win/30 bg-win/15' : 'border border-surface-muted bg-surface'}`}>
            <span className={`font-medium ${i === 0 ? 'text-win' : 'text-brand-ice'}`}>{displayName(row.playerId)}</span>
            <div className="flex gap-4 text-sm text-brand-ice/70">
              <span>{row.setsWon} set</span>
              <span>{row.gamesWon} game</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderClassicSets = () => sets.map((s, i) => renderSetCard(s, i));

  const renderKingSets = () => (
    <>
      {sets.map((s, i) => renderSetCard(s, i))}
      {renderKingSummary(sets, 'Riepilogo King')}
    </>
  );

  const renderSuperKingSets = () => {
    const goldKingSets = sets.filter((s) => s[SET_FIELDS.FIELD] === SET_FIELD_VALUES.GOLD_KING);
    const silverKingSets = sets.filter((s) => s[SET_FIELDS.FIELD] === SET_FIELD_VALUES.SILVER_KING);
    const goldFinalSets = sets.filter((s) => s[SET_FIELDS.FIELD] === SET_FIELD_VALUES.GOLD_FINAL);
    const silverFinalSets = sets.filter((s) => s[SET_FIELDS.FIELD] === SET_FIELD_VALUES.SILVER_FINAL);
    return (
      <>
        <h2 className="mt-4 mb-2 font-display text-lg font-semibold text-gold">King Campo Gold</h2>
        {goldKingSets.map((s, i) => renderSetCard(s, i, `Set ${i + 1} (Gold King)`))}
        {goldKingSets.length > 0 && renderKingSummary(goldKingSets, 'Riepilogo King Gold')}

        <h2 className="mt-4 mb-2 font-display text-lg font-semibold text-brand-ice/60">King Campo Silver</h2>
        {silverKingSets.map((s, i) => renderSetCard(s, i, `Set ${i + 1} (Silver King)`))}
        {silverKingSets.length > 0 && renderKingSummary(silverKingSets, 'Riepilogo King Silver')}

        <h2 className="mt-4 mb-2 font-display text-lg font-semibold text-gold">Finale King Gold</h2>
        {goldFinalSets.map((s, i) => renderSetCard(s, i, `Set ${i + 1} (Finale Gold)`))}
        {goldFinalSets.length > 0 && renderKingSummary(goldFinalSets, 'Riepilogo Finale Gold')}

        <h2 className="mt-4 mb-2 font-display text-lg font-semibold text-brand-ice/60">Finale King Silver</h2>
        {silverFinalSets.map((s, i) => renderSetCard(s, i, `Set ${i + 1} (Finale Silver)`))}
        {silverFinalSets.length > 0 && renderKingSummary(silverFinalSets, 'Riepilogo Finale Silver')}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <div className="mb-2 flex items-center gap-2">
        <Button type="button" variant="outline" onClick={() => navigate(backUrl)} className="w-fit">
          <IconArrowLeft className="mr-2 h-4 w-4" />
          Torna al gruppo
        </Button>
      </div>
      <h1 className="font-display text-xl font-bold text-brand-ice">Partita del {formatDate(playedAt)}</h1>

      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <span className={`inline-flex rounded-badge border px-2.5 py-0.5 text-xs font-semibold ${matchTypeBadgeClass(matchType)}`}>
          {MATCH_TYPE_LABELS[matchType] || matchType}
        </span>
        <span className={`inline-flex rounded-badge border px-2.5 py-0.5 text-xs font-semibold ${
          matchStatus === 'approved' ? 'border-win/40 bg-win/15 text-win' :
          matchStatus === 'pending_approval' ? 'border-gold/40 bg-gold/15 text-gold' :
          'border-loss/40 bg-loss/15 text-loss'
        }`}>
          {MATCH_STATUS_LABELS[matchStatus] || matchStatus}
        </span>
      </div>

      {matchStatus === MATCH_STATUS.PENDING_APPROVAL && (
        <Card className="border-brand-royal/30 bg-brand-royal/10">
          <CardContent className="space-y-3 p-4">
            <h2 className="font-display text-base font-semibold text-brand-ice">
            Approvazioni: {approvals.length}/{requiredApprovals}
            </h2>
          {approvals.length > 0 && (
              <div className="flex flex-wrap gap-2">
              {approvals.map((a) => (
                  <Badge key={a.userId} variant="win">{displayName(a.userId)}</Badge>
              ))}
              </div>
          )}
          {canApprove && (
              <Button type="button" onClick={handleApprove} disabled={approveLoading}>
              {approveLoading ? 'Approvazione...' : 'Approva'}
            </Button>
          )}
          {hasAlreadyApproved && (
              <p className="text-sm text-brand-ice/70">Hai già approvato</p>
          )}
          </CardContent>
        </Card>
      )}

      <h2 className="font-display text-lg font-semibold text-brand-ice">Set</h2>
      {matchType === MATCH_TYPES.CLASSIC && renderClassicSets()}
      {matchType === MATCH_TYPES.KING && renderKingSets()}
      {matchType === MATCH_TYPES.SUPER_KING && renderSuperKingSets()}

      <div className="my-5 border-t border-surface-muted" />

      <h2 className="font-display text-lg font-semibold text-brand-ice">Riepilogo giocatori</h2>
      <div className="space-y-2">
        {playerStats.map((row) => (
          <div key={row.playerId} className="flex items-center gap-3 rounded-card border border-surface-muted bg-surface-card px-4 py-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: isGuest(row.playerId) ? '#64748B' : avatarColorFromUserId(row.playerId) }}
            >
              {getInitials(displayName(row.playerId))}
            </div>
            <span className="flex-1 font-medium text-brand-ice">{displayName(row.playerId)}</span>
            <div className="flex gap-4 text-right text-sm">
              <div><p className="font-semibold text-brand-ice">{row.setsPlayed}</p><p className="text-xs text-brand-ice/50">Giocati</p></div>
              <div><p className="font-semibold text-brand-ice">{row.setsWon}</p><p className="text-xs text-brand-ice/50">Vinti</p></div>
              <div><p className="font-semibold text-brand-ice">{row.gamesWon}</p><p className="text-xs text-brand-ice/50">Game</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
